import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { useCallback, useState } from 'react';
import type { DocumentFile, DocumentFolder } from '../../types';
import type { SelectableItem } from './useMultiselect';

/** Shape attached to every draggable item's data payload. */
export interface DragItemData {
  type: 'file' | 'folder';
  item: DocumentFile | DocumentFolder;
}

/**
 * The droppable id used for the root-level breadcrumb target.
 * Must not clash with any real folder id (which are UUIDs).
 */
export const ROOT_DROP_ID = '__root__';

interface UseDragAndDropOptions {
  /** Called when the drag gesture starts so the explorer can dim non-targets. */
  onDragStart: () => void;
  /** Called when the drag gesture ends (success or cancel). */
  onDragEnd: () => void;
  /** All currently selected items, used to detect a multi-selection drag. */
  selectedItems: SelectableItem[];
  /** Returns true when the item with the given id is in the current selection. */
  isItemSelected: (id: string) => boolean;
  /**
   * Called instead of the single-item path when the user drags an item that is
   * part of a multi-selection. The caller is responsible for moving all items
   * and clearing the selection afterwards.
   */
  onMultiDrop: (items: SelectableItem[], targetFolderId: string | null) => void;
}

export interface UseDragAndDropReturn {
  /** True while a drag is in progress – used to show drop-zone highlights. */
  isDragging: boolean;
  /** The item currently being dragged (null when idle). */
  draggedItem: DragItemData | null;
  /**
   * True when the dragged item belongs to the current selection and more than
   * one item is selected — i.e. this drag will move all selected items.
   */
  isDraggingSelection: boolean;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
}

/**
 * Manages internal drag-and-drop state and executes move API calls when an
 * item is dropped onto a valid folder target or breadcrumb crumb.
 *
 * Valid drop targets are folder rows (droppable id = folder.id) and breadcrumb
 * crumbs (droppable id = folder.id or ROOT_DROP_ID for the root level).
 *
 * Self-drop and same-parent drops are no-ops. Deeper cycle detection is left
 * to the server – on a 400 error the optimistic removal is reverted and a toast
 * is shown with the server's message.
 *
 * Multi-selection drag: when the dragged item is part of the current selection
 * (and the selection contains more than one item), `onMultiDrop` is called
 * with all selected items instead of executing a single-item move.
 */
export function useDragAndDrop({
  onDragStart,
  onDragEnd,
  selectedItems,
  isItemSelected,
  onMultiDrop,
}: UseDragAndDropOptions): UseDragAndDropReturn {

  const [isDragging, setIsDragging] = useState(false);
  const [draggedItem, setDraggedItem] = useState<DragItemData | null>(null);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const data = event.active.data.current as DragItemData | undefined;
      if (!data) return;

      setIsDragging(true);
      setDraggedItem(data);
      onDragStart();
    },
    [onDragStart]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setIsDragging(false);
      setDraggedItem(null);
      onDragEnd();

      const data = event.active.data.current as DragItemData | undefined;
      if (!data || !event.over) return;

      const rawTargetId = event.over.id as string;
      const targetFolderId = rawTargetId === ROOT_DROP_ID ? null : rawTargetId;

      const { type, item } = data;

      // Self-drop guard — prevents dropping a folder onto itself
      if (type === 'folder' && item.id === targetFolderId) return;

      // Multi-selection drag: delegate to the caller so bulk move logic runs
      const isDraggingMultiple = isItemSelected(item.id) && selectedItems.length > 1;
      if (isDraggingMultiple) {
        onMultiDrop(selectedItems, targetFolderId);
        return;
      }

      onMultiDrop([item], targetFolderId);
      return;
    },
    [onDragEnd, isItemSelected, selectedItems, onMultiDrop]
  );

  // True when the in-progress drag will move the whole selection (not just one item)
  const isDraggingSelection =
    isDragging && draggedItem !== null && isItemSelected(draggedItem.item.id) && selectedItems.length > 1;

  return { isDragging, draggedItem, isDraggingSelection, handleDragStart, handleDragEnd };
}
